// ============================================================================
// OnboardingList — list view of /onboarding with multi-select + bulk actions:
//   • Send Reminder  → ReminderModal for the selected records
//   • Mark Item Complete → pick one of the 16 items; sets it complete on every
//     selected record where the item is in applies_to (skips others)
//   • Export CSV     → one row per selected record (spec column set)
// Spec §4.
// ============================================================================
import { useMemo, useState } from 'react';
import { Card, Button, useToast } from '../../shared/components';
import {
  effectiveChecklist,
  computeProgress,
  missingItemsSummary,
  mergeChecklistIntoRaw,
  fmtDateShort,
  csvEscape,
} from './utils';
import {
  canonicalSeed,
  stageLabel,
  TYPE_CHIP_LABELS,
  CHECKLIST_SECTIONS,
  type CanonicalChecklist,
  type OnboardingRecord,
} from './types';
import ReminderModal from './reminder-modal';

interface Props {
  records: OnboardingRecord[];
  canWrite: boolean;
  onSelect: (rec: OnboardingRecord) => void;
  onPatch: (
    rec: OnboardingRecord,
    dbPatch: Record<string, unknown>,
    localPatch: Partial<OnboardingRecord>,
  ) => Promise<void>;
}

function keyOf(r: OnboardingRecord) {
  return `${r.source}:${r.onboarding_id}`;
}

// Flat list of every checklist item (key + label) from the canonical seed.
const ALL_ITEMS = (() => {
  const seed = canonicalSeed();
  const out: { key: string; label: string }[] = [];
  for (const section of CHECKLIST_SECTIONS) {
    for (const it of seed[section]) out.push({ key: it.key, label: it.label });
  }
  return out;
})();

export default function OnboardingList({ records, canWrite, onSelect, onPatch }: Props) {
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reminderOpen, setReminderOpen] = useState(false);
  const [markItemKey, setMarkItemKey] = useState('');
  const [busy, setBusy] = useState(false);

  const selectedRecords = useMemo(
    () => records.filter((r) => selected.has(keyOf(r))),
    [records, selected],
  );

  const allChecked = records.length > 0 && selected.size === records.length;

  function toggle(r: OnboardingRecord) {
    setSelected((s) => {
      const n = new Set(s);
      const k = keyOf(r);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  }
  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(records.map(keyOf)));
  }

  async function markItemComplete() {
    if (!markItemKey || selectedRecords.length === 0) return;
    setBusy(true);
    let applied = 0;
    let skipped = 0;
    for (const rec of selectedRecords) {
      const checklist = effectiveChecklist(rec.checklist);
      const type = rec.employmentTypeForChecklist;
      // Only sections that contain the item, and only if applies_to matches.
      let touched = false;
      const next: CanonicalChecklist = {
        documents: [...checklist.documents],
        compliance: [...checklist.compliance],
        training: [...checklist.training],
        admin: [...checklist.admin],
      };
      for (const section of CHECKLIST_SECTIONS) {
        next[section] = next[section].map((it) => {
          if (it.key === markItemKey && it.applies_to.includes(type)) {
            touched = true;
            return { ...it, status: 'complete' as const };
          }
          return it;
        });
      }
      if (!touched) {
        skipped += 1;
        continue;
      }
      const merged = mergeChecklistIntoRaw(rec.rawChecklistJson, next);
      try {
        await onPatch(rec, { checklist_json: merged }, { checklist: next, rawChecklistJson: merged });
        applied += 1;
      } catch {
        skipped += 1;
      }
    }
    setBusy(false);
    setMarkItemKey('');
    toast.success(`Marked complete on ${applied} record(s)`, {
      description: skipped ? `${skipped} skipped (not applicable or denied).` : undefined,
    });
  }

  function exportCsv() {
    const header = [
      'onboarding_id',
      'name',
      'type',
      'role',
      'account',
      'start_date',
      'stage',
      'percent_complete',
      'missing_items',
    ];
    const lines = [header.join(',')];
    for (const rec of selectedRecords) {
      const checklist = effectiveChecklist(rec.checklist);
      const pct = computeProgress(checklist, rec.employmentTypeForChecklist);
      const missing = missingItemsSummary(checklist, rec.employmentTypeForChecklist).join('; ');
      lines.push(
        [
          csvEscape(rec.onboarding_id),
          csvEscape(rec.name),
          csvEscape(TYPE_CHIP_LABELS[rec.type]),
          csvEscape(rec.role),
          csvEscape(rec.account),
          csvEscape(rec.start_date),
          csvEscape(stageLabel(rec.stage, rec.type)),
          csvEscape(pct),
          csvEscape(missing),
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedRecords.length} record(s)`);
  }

  return (
    <div className="space-y-3">
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2">
          <span className="text-xs font-medium text-tcs-text">{selected.size} selected</span>
          <Button size="sm" variant="secondary" onClick={() => setReminderOpen(true)}>
            Send reminder
          </Button>
          <div className="flex items-center gap-1">
            <select
              value={markItemKey}
              onChange={(e) => setMarkItemKey(e.target.value)}
              disabled={!canWrite}
              className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-xs disabled:opacity-50"
            >
              <option value="">Mark item complete…</option>
              {ALL_ITEMS.map((it) => (
                <option key={it.key} value={it.key}>
                  {it.label}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="secondary"
              disabled={!canWrite || !markItemKey || busy}
              loading={busy}
              onClick={markItemComplete}
            >
              Apply
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={exportCsv}>
            Export CSV
          </Button>
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="min-w-full divide-y divide-tcs-border text-sm">
          <thead className="bg-tcs-surface text-left text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            <tr>
              <th className="px-3 py-2">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all" />
              </th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Role / Account</th>
              <th className="px-3 py-2">Start</th>
              <th className="px-3 py-2">Stage</th>
              <th className="px-3 py-2">Progress</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tcs-border">
            {records.map((r) => {
              const pct = computeProgress(effectiveChecklist(r.checklist), r.employmentTypeForChecklist);
              return (
                <tr key={keyOf(r)} className="hover:bg-tcs-surface">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(keyOf(r))}
                      onChange={() => toggle(r)}
                      aria-label={`Select ${r.name}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onSelect(r)}
                      className="font-medium text-tcs-text hover:underline"
                    >
                      {r.name}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs">{TYPE_CHIP_LABELS[r.type]}</td>
                  <td className="px-3 py-2 text-xs text-tcs-text-muted">
                    {[r.role, r.account].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDateShort(r.start_date)}</td>
                  <td className="px-3 py-2 text-xs">{stageLabel(r.stage, r.type)}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{pct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {reminderOpen && <ReminderModal records={selectedRecords} onClose={() => setReminderOpen(false)} />}
    </div>
  );
}
